// src/pages/admin/site/AdminSiteEffectsPage.js
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';
import { FaSnowflake, FaCloudRain, FaLeaf, FaMagic } from 'react-icons/fa';

function AdminSiteEffectsPage() {
    const [loading, setLoading] = useState(true); // Start loading
    const [effects, setEffects] = useState([]);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const loadEffects = async () => {
        setLoading(true);
        try {
            const result = await pb.collection('site_seasonal_effects').getList(1, 50, { sort: '-created' });
            setEffects(result.items);
        } catch (err) {
            console.error("Erreur chargement effets", err);
            // Si erreur 404 (collection n'existe pas), on ne fait rien de spécial, la liste sera vide
            if (err.status === 404) {
                setError("La collection 'site_seasonal_effects' n'existe pas encore. Veuillez la créer dans Pocketbase (Nom: site_seasonal_effects, Champs: name (text), type (select: snow, rain, leaves), is_active (bool), intensity (number)).");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEffects();
    }, []);

    const handleCreateDefault = async () => {
        setLoading(true);
        setMessage('');
        setError('');
        try {
            // Créer les effets par défaut
            await pb.collection('site_seasonal_effects').create({
                name: 'Neige Hivernale',
                type: 'snow',
                is_active: false,
                intensity: 50
            });
            await pb.collection('site_seasonal_effects').create({
                name: 'Pluie d\'Automne',
                type: 'rain',
                is_active: false,
                intensity: 100
            });
            await pb.collection('site_seasonal_effects').create({
                name: 'Feuilles d\'Automne',
                type: 'leaves',
                is_active: false,
                intensity: 30
            });
            setMessage("Effets par défaut créés avec succès !");
            loadEffects();
        } catch (err) {
            setError("Erreur lors de la création : " + err.message);
            setLoading(false);
        }
    };

    const toggleEffect = async (effect) => {
        try {
            // Désactiver tous les autres si on active celui-ci
            const isActivating = !effect.is_active;

            if (isActivating) {
                // On doit d'abord tout désactiver pour n'en avoir qu'un seul actif (optionnel, mais mieux pour éviter surcharge)
                // Note: faire ça en boucle n'est pas atomique mais suffisant ici
                for (let e of effects) {
                    if (e.is_active && e.id !== effect.id) {
                        await pb.collection('site_seasonal_effects').update(e.id, { is_active: false });
                    }
                }
            }

            await pb.collection('site_seasonal_effects').update(effect.id, { is_active: isActivating });
            loadEffects();
        } catch (err) {
            alert("Erreur: " + err.message);
        }
    };

    const iconMap = {
        'snow': <FaSnowflake />,
        'rain': <FaCloudRain />,
        'leaves': <FaLeaf />,
        'default': <FaMagic />
    };

    return (
        <AdminLayout title="Effets Visuels Saisonniers">
            <div className="admin-page-header">
                <h2>Gestion des effets de saison</h2>
                <p>Activez des effets visuels sur tout le site (neige, pluie, etc.) pour des événements spéciaux.</p>
            </div>

            {error && <div className="alert alert-danger" style={{ color: 'red', marginBottom: 20 }}>{error}</div>}
            {message && <div className="alert alert-success" style={{ color: 'green', marginBottom: 20 }}>{message}</div>}

            {loading ? (
                <div>Chargement...</div>
            ) : (
                <>
                    {effects.length === 0 && !error && (
                        <div style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '10px' }}>
                            <p>Aucun effet configuré.</p>
                            <button className="btn btn-primary" onClick={handleCreateDefault}>
                                Initialiser les effets par défaut
                            </button>
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginTop: '20px' }}>
                        {effects.map(effect => (
                            <div key={effect.id} className="card" style={{
                                padding: '20px',
                                border: effect.is_active ? '2px solid var(--primary-color)' : '1px solid #ddd',
                                backgroundColor: effect.is_active ? '#f0f7ff' : '#fff'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                                    <div style={{ fontSize: '2rem', color: 'var(--primary-color)' }}>
                                        {iconMap[effect.type] || iconMap['default']}
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{effect.name}</h3>
                                        <span style={{ fontSize: '0.85rem', color: '#666', textTransform: 'uppercase' }}>{effect.type}</span>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 'bold', color: effect.is_active ? 'green' : '#888' }}>
                                        {effect.is_active ? 'ACTIF' : 'INACTIF'}
                                    </span>
                                    <button
                                        className={`btn ${effect.is_active ? 'btn-secondary' : 'btn-primary'}`}
                                        onClick={() => toggleEffect(effect)}
                                    >
                                        {effect.is_active ? 'Désactiver' : 'Activer'}
                                    </button>
                                </div>
                                <div style={{ marginTop: '10px', fontSize: '0.8rem', opacity: 0.7 }}>
                                    Intensité: {effect.intensity}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </AdminLayout>
    );
}

export default AdminSiteEffectsPage;
